import { uuid } from '../uuid';

import { Channel, Socket } from 'phoenix';

import {
  mergeTurn,
  type BotOption,
  type BotTurn,
  type IncomingFrame,
} from './bot-turn';
import type { EngagementSession } from './session';

/**
 * Drive a real conversation with a deployed solution, server-side, over the
 * Engagement API's Phoenix channel.
 *
 * WHY THE SOCKET AND NOT REST. The Engagement API's REST endpoints
 * (`/chats/{id}/start` and `/message`) answer 202 with no body — bot replies
 * come back out-of-band to a webhook URL registered on the session. That
 * needs a publicly reachable callback, which neither a Vite dev server on
 * localhost nor a single serverless invocation has. The socket delivers
 * replies on the same connection that sent the message, which is exactly the
 * shape a request/response route can hold.
 *
 * The wire shapes are pype's, read off `src/xstate/helpers/phoenix.ts`. They
 * are duplicated rather than imported: pype is a browser bundle whose
 * `exports` point at a gitignored `dist`, and pro-studio treats it as a black
 * box. A change to those shapes must be mirrored here — the tests pin them.
 */

/** Phoenix has no connect timeout of its own, and `onError` does not fire for a stalled handshake. */
const SOCKET_OPEN_TIMEOUT_MS = 10_000;

/** Bound on join + `chat:start` + `chat:ready`. */
const HANDSHAKE_TIMEOUT_MS = 20_000;

/** How long one turn waits for its FIRST content frame. */
const REPLY_TIMEOUT_MS = 30_000;

/**
 * Quiet window after a turn's first frame. The bot routinely splits a reply
 * across several frames; returning on the first one lets a straggler arrive
 * during the NEXT turn and be attributed to the wrong question.
 */
const REPLY_SETTLE_MS = 1_500;

/**
 * How long to wait for an unprompted greeting before deciding there is none.
 *
 * The widget receives its greeting on this same socket shortly after
 * `chat:ready`; there is no "no greeting" signal, so absence can only be
 * observed as silence.
 */
const GREETING_TIMEOUT_MS = 6_000;

export type { BotTurn, BotOption } from './bot-turn';

export class ConversationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversationError';
  }
}

export interface Conversation {
  chatId: string;
  /**
   * Whatever the bot says unprompted after `chat:ready`, or null if it says
   * nothing. Not every solution has a greeting, so an absent one is a fact to
   * record, not a failure — and it must be drained before the first user
   * message either way, or it lands in that message's reply window and is
   * attributed to a question it does not answer.
   */
  greeting: () => Promise<BotTurn | null>;
  /** Send one user message and return the bot's whole reply. */
  send: (text: string) => Promise<BotTurn>;
  /**
   * Pick one of the options the bot offered, the way the widget does — an
   * `embed` carrying the option's value, not its label typed as text. A
   * decision node routes on `bot_value`, so typing the label only works
   * when the label happens to match an example phrase.
   */
  sendOption: (option: BotOption) => Promise<BotTurn>;
  /**
   * What a webview hands the widget when it's submitted
   * (`PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH:<json>`), sent the way the widget
   * sends it — pype's `pushMetadataSend`, `metadata:send`. Silent: the bot
   * reads it with the next message (the webview's close message, see
   * `sendPlain`), so no reply is waited for here.
   */
  sendMetadata?: (data: Record<string, unknown> | unknown[]) => Promise<void>;
  /**
   * A plain-text message with no app object — what the widget sends when a
   * webview closes (pype's `onOverlayDismiss`: the webview's `close_message`,
   * else "Close") — and the bot's reply.
   */
  sendPlain?: (text: string) => Promise<BotTurn>;
  /**
   * End the chat on the backend (`chat:end`), then `close()`. Best-effort:
   * the socket is torn down whether or not the backend acknowledges.
   */
  end: () => Promise<void>;
  /** Idempotent. Always call it — an open socket outlives the request otherwise. */
  close: () => void;
  /**
   * Whether the chat is over — the bot's flow ended it (`chat:ended`) or
   * `end()` ran. A caller finishing a conversation reads this rather than
   * waiting for its next send to be refused.
   */
  isEnded: () => boolean;
  /**
   * Whether the chat has been handed to a live agent: the platform said it is
   * waiting for one, or one joined. From then on the bot is not the one
   * reading, so `send` sends plain text and does not wait for a bot reply.
   */
  isWithAgent: () => boolean;
  /**
   * Messages that arrive while no turn is waiting for them — above all a live
   * agent's replies, which answer on the agent's time, not the user's — handed
   * over as they settle. Also an "agent joined" notice, as a `system` turn.
   * Starts after the first turn (the greeting) has been collected, so it never
   * takes one. Returns the unsubscribe.
   */
  onUnprompted: (listener: (turn: BotTurn) => void) => () => void;
}

/** Shown when a live agent joins — the platform sends a notice, not a message. */
const AGENT_JOINED_TEXT = 'A live agent joined the conversation.';

/**
 * `incoming:notice`, reduced to the agent hand-off fields pype reads
 * (`mapNoticeToEvents` in pype's `phoenix.ts`).
 */
interface IncomingNotice {
  detail?: string;
  data?: {
    status?: string;
    participant_changed?: string;
    agent_is_assigned?: boolean;
  } | null;
}

/** How long `end()` waits for `chat:end` to be acknowledged before closing anyway. */
const END_TIMEOUT_MS = 5_000;

export interface OpenConversationOptions {
  appId: string;
  session: EngagementSession;
  engagementApiUrl: string;
  /** Injected in tests. Node has had a global `WebSocket` since 22. */
  transport?: unknown;
  replyTimeoutMs?: number;
  settleMs?: number;
  /**
   * How long to wait for an unprompted greeting. Short: a solution with no
   * greeting pays this on every run, and the widget's own experience is that
   * one either arrives promptly after `chat:ready` or not at all.
   */
  greetingTimeoutMs?: number;
  /** Bound on join + `chat:start` + `chat:ready`. Injected in tests. */
  handshakeTimeoutMs?: number;
  /**
   * The run's overall budget, as an abort signal.
   *
   * The per-step timeouts above bound each step; this bounds the WHOLE
   * conversation. Without it a run's own deadline (`RUN_TIMEOUT_MS` in the run
   * route) covers only session creation, and a serverless invocation is killed
   * by the platform with no message for the caller.
   */
  signal?: AbortSignal;
  /**
   * Sends the customer's messages some other way than `msg:send` on the
   * socket — replies still arrive on the socket. Validate's model comparison
   * posts them through Pro Studio's server with an `x-ai-eg-model` header,
   * which the browser can't add itself (the Engagement API's CORS doesn't
   * allow it). Gets the same payload `msg:send` would carry.
   */
  sendVia?: (
    payload: Record<string, unknown>,
    session: EngagementSession
  ) => Promise<void>;
}

/** The reason a run stopped, phrased for a caller who did not set the budget. */
const abortError = () =>
  new ConversationError(
    'The run exceeded its time budget before the conversation finished.'
  );

const connectSocket = (
  engagementApiUrl: string,
  accessToken: string,
  transport: unknown
): Promise<Socket> =>
  new Promise((resolve, reject) => {
    const socketUrl = `${engagementApiUrl.replace(/^http/, 'ws').replace(/\/$/, '')}/socket`;

    const socket = new Socket(socketUrl, {
      params: { token: accessToken },
      transport: transport as never,
      // One run, one connection. Phoenix's own reconnect would quietly
      // resurrect a socket this code has already decided to abandon, and a
      // serverless invocation has no lifetime to reconnect into.
      reconnectAfterMs: () => 2_147_483_647,
    });

    // Everything below fires at most once. `onError` is NOT unregistered
    // after the socket opens — Phoenix keeps calling it for every later
    // failure — so without this guard a mid-conversation blip ran the
    // teardown path of a promise that had already resolved: it disconnected
    // the live socket while a turn was being collected, and the rejection was
    // dropped. The caller then saw "The bot did not reply within 30s", which
    // names neither the cause nor the fix. After open, the conversation owns
    // the socket and its own timeouts report the failure.
    let settled = false;

    // A socket that neither opens nor errors would leave this pending
    // forever, and the caller has no reference to disconnect. Own it here.
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      socket.disconnect();
      reject(new ConversationError(`Socket did not open: ${socketUrl}`));
    }, SOCKET_OPEN_TIMEOUT_MS);

    socket.onOpen(() => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(socket);
    });
    socket.onError((err: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Disconnect before rejecting. The caller never receives this socket, so
      // this is the only reference to it: leaving it connected keeps the Node
      // event loop alive and bills a serverless invocation for a connection
      // nothing will ever read from.
      socket.disconnect();
      reject(
        new ConversationError(
          `Socket error on ${socketUrl}: ${
            err instanceof Error ? err.message : String(err)
          }`
        )
      );
    });

    socket.connect();
  });

const joinChannel = (socket: Socket, session: EngagementSession) =>
  new Promise<Channel>((resolve, reject) => {
    const channel = socket.channel(`chat_v2:${session.chatId}`, {
      user_id: session.userId,
    });

    channel
      .join()
      .receive('ok', () => resolve(channel))
      .receive('error', (resp: unknown) =>
        reject(
          new ConversationError(`Channel join failed: ${JSON.stringify(resp)}`)
        )
      )
      .receive('timeout', () =>
        reject(new ConversationError('Channel join timed out'))
      );
  });

/**
 * `chat:start` then `chat:ready`.
 *
 * `chat:ready` is the real provisioning gate on a fresh conversation — the
 * ack only means "accepted", and pushing a message before ready reaches an
 * unprovisioned chat (the REST equivalent answers 428).
 */
const startChat = (
  channel: Channel,
  appId: string,
  session: EngagementSession
) =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    const settle = (err?: Error) => {
      if (settled) return;
      settled = true;
      channel.off('chat:ready', readyRef);
      if (err) reject(err);
      else resolve();
    };

    const readyRef = channel.on('chat:ready', () => settle());

    channel
      .push('chat:start', {
        app_id: appId,
        consumer: session.userId,
        gateway: 'pypestream_widget',
        pype_id: session.pypeId,
        stream_id: session.streamId,
        user_id: session.userId,
        version: 'v2',
        access_token: session.accessToken,
      })
      .receive('error', (payload: unknown) => {
        // Two shapes mean the same thing: the conversation is already
        // running, so nothing is being provisioned and no `chat:ready` is
        // coming. Older backends answer this way; newer ones plainly ack.
        if (
          (payload as { errors?: { detail?: string } })?.errors?.detail ===
          'Existing active conversation'
        ) {
          settle();
        } else {
          settle(
            new ConversationError(
              `chat:start failed: ${JSON.stringify(payload)}`
            )
          );
        }
      })
      .receive('timeout', () =>
        settle(new ConversationError('chat:start timed out'))
      );
  });

export const openConversation = async ({
  appId,
  session,
  engagementApiUrl,
  transport = globalThis.WebSocket,
  replyTimeoutMs = REPLY_TIMEOUT_MS,
  settleMs = REPLY_SETTLE_MS,
  greetingTimeoutMs = GREETING_TIMEOUT_MS,
  handshakeTimeoutMs = HANDSHAKE_TIMEOUT_MS,
  signal,
  sendVia,
}: OpenConversationOptions): Promise<Conversation> => {
  if (signal?.aborted) throw abortError();

  const socket = await connectSocket(
    engagementApiUrl,
    session.accessToken,
    transport
  );

  // The signal is checked BETWEEN steps, not only on entry. `connectSocket`
  // and the handshake below do not observe it, so a run aborted while the
  // socket was opening would otherwise go on to join the channel and push
  // `chat:start` — a real conversation on a channel the caller has already
  // walked away from, plus a socket held open until the first turn times out.
  if (signal?.aborted) {
    socket.disconnect();
    throw abortError();
  }

  // Frames are buffered from the moment the channel exists, not from the
  // moment a turn starts waiting: the bot's greeting arrives while the
  // handshake is still settling, and a listener attached later misses it.
  const inbox: IncomingFrame[] = [];
  let notify: (() => void) | undefined;
  let ended = false;

  // Live-agent hand-off, and the frames nothing is waiting for (see
  // `onUnprompted`). `collecting` is true while a turn owns the inbox.
  let withAgent = false;
  let collecting = false;
  let turnsStarted = false;
  let unprompted: ((turn: BotTurn) => void) | undefined;
  // Notices that land while a turn is being collected wait for it, so "agent
  // joined" is not shown above the bot's "let me connect you".
  const pendingNotices: BotTurn[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  // Assigned once the settle window is known, below.
  let scheduleFlush = () => {};

  // Owned so it can be CLEARED once the race is decided. An uncleared timer
  // keeps the Node event loop alive for its full duration after the run has
  // already answered — which on a serverless invocation is up to
  // `handshakeTimeoutMs` of billed idle on every successful run.
  let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

  let channel: Channel;
  try {
    channel = await Promise.race([
      (async () => {
        const joined = await joinChannel(socket, session);

        joined.on('incoming:msg', (frame: IncomingFrame) => {
          inbox.push(frame);
          notify?.();
          scheduleFlush();
        });
        joined.on('incoming:notice', (notice: IncomingNotice) => {
          if (notice?.detail !== 'chat' || !notice.data) return;
          const { status, participant_changed, agent_is_assigned } =
            notice.data;
          if (status === 'waiting_for_agent_assign') withAgent = true;
          if (
            participant_changed === 'agent_added' &&
            agent_is_assigned === true
          ) {
            withAgent = true;
            pendingNotices.push({
              text: AGENT_JOINED_TEXT,
              options: [],
              frames: [notice],
              system: true,
            });
            scheduleFlush();
          }
          if (participant_changed === 'agent_left') withAgent = false;
        });
        joined.on('chat:ended', () => {
          ended = true;
          notify?.();
        });

        await startChat(joined, appId, session);
        return joined;
      })(),
      new Promise<never>((_, reject) => {
        handshakeTimer = setTimeout(
          () => reject(new ConversationError('Channel handshake timed out')),
          handshakeTimeoutMs
        );
      }),
    ]);
  } catch (err) {
    socket.disconnect();
    throw err;
  } finally {
    clearTimeout(handshakeTimer);
  }

  // Same check on the other side of the handshake: aborting mid-handshake must
  // not leave a joined channel and a live socket behind.
  if (signal?.aborted) {
    try {
      channel.leave();
    } catch {
      // A dead socket throws here; the disconnect is what matters.
    }
    socket.disconnect();
    throw abortError();
  }

  /** Wait for a frame to land, or for `ms` to pass. Resolves either way. */
  let pendingWaitTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * Resolves the wait that is currently in flight, if there is one. `close()`
   * needs it: clearing the timer and dropping `notify` would otherwise leave
   * that promise pending forever, and whatever was awaiting it — a turn being
   * collected when the run is torn down — never resumes and never settles.
   */
  let releaseWait: (() => void) | undefined;
  const waitForFrame = (ms: number) =>
    new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        pendingWaitTimer = undefined;
        releaseWait = undefined;
        notify = undefined;
        resolve();
      };
      const timer = setTimeout(finish, ms);
      pendingWaitTimer = timer;
      releaseWait = finish;
      notify = finish;
    });

  /**
   * Collect one turn: wait for the first content frame, then keep collecting
   * while frames keep arriving inside the quiet window.
   *
   * `required` is the difference between a reply and a greeting. A reply that
   * never comes is a failed run; a greeting that never comes is a solution
   * without one.
   */
  /**
   * When the turn being collected got its first content frame. Measured at
   * the moment the frame is taken off the inbox — up to one 500ms poll after
   * it landed only if nothing woke the wait, and `notify` wakes it on every
   * frame, so in practice this is arrival time.
   */
  let firstFrameAt: number | undefined;

  /**
   * Whether the bot has spoken yet, which decides how typed text is shaped
   * (see `send`). Only a collected turn with content sets it: `collectTurn`
   * returns early, before this, when no frame arrived.
   */
  let botSpoke = false;

  scheduleFlush = () => {
    if (collecting || !turnsStarted || !unprompted) return;
    clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = undefined;
      if (collecting || !unprompted) return;
      for (const notice of pendingNotices.splice(0)) unprompted(notice);
      if (inbox.length === 0) return;
      const turn = mergeTurn(inbox.splice(0));
      if (turn.frames.length) unprompted(turn);
    }, settleMs);
  };

  const collectTurn = async (args: {
    required: boolean;
    timeoutMs: number;
  }): Promise<BotTurn | null> => {
    collecting = true;
    turnsStarted = true;
    clearTimeout(flushTimer);
    try {
      return await collectOneTurn(args);
    } finally {
      collecting = false;
      if (inbox.length || pendingNotices.length) scheduleFlush();
    }
  };

  const collectOneTurn = async ({
    required,
    timeoutMs,
  }: {
    required: boolean;
    timeoutMs: number;
  }): Promise<BotTurn | null> => {
    const collected: IncomingFrame[] = [];
    const deadline = Date.now() + timeoutMs;
    firstFrameAt = undefined;

    // First frame — the long wait.
    while (mergeTurn(collected).frames.length === 0) {
      collected.push(...inbox.splice(0));
      if (mergeTurn(collected).frames.length > 0) {
        firstFrameAt = Date.now();
        break;
      }

      if (ended) {
        if (!required) return null;
        throw new ConversationError(
          'The conversation ended before the bot replied. The solution may not be published for this channel, or the flow ends here.'
        );
      }
      if (Date.now() >= deadline) {
        if (!required) return null;
        throw new ConversationError(
          `The bot did not reply within ${Math.round(timeoutMs / 1000)}s.`
        );
      }
      if (signal?.aborted) {
        if (!required) return null;
        throw abortError();
      }
      // eslint-disable-next-line no-await-in-loop
      await waitForFrame(Math.min(500, deadline - Date.now()));
    }

    // Stragglers — the short wait, repeated while it keeps paying off, but
    // never past this turn's own deadline.
    //
    // The bound is not decoration. A frame re-arms the wait even when
    // `mergeTurn` discards it, and `analytics_update` frames are discarded —
    // so a solution emitting one more often than `settleMs` kept this loop
    // alive indefinitely, which no other timeout could interrupt.
    for (;;) {
      const remaining = deadline - Date.now();
      if (remaining <= 0 || signal?.aborted) break;
      // eslint-disable-next-line no-await-in-loop
      await waitForFrame(Math.min(settleMs, remaining));
      if (inbox.length === 0) break;
      collected.push(...inbox.splice(0));
    }

    const turn = mergeTurn(collected);
    botSpoke = true;
    return turn;
  };

  const close = () => {
    // The in-flight wait is RELEASED, not just cancelled: a live timer holds
    // the event loop open past close(), but clearing it without resolving
    // the promise it belongs to strands whoever is awaiting it — a turn
    // being collected when the run is torn down would hang rather than
    // finish. `releaseWait` clears the timer and resolves.
    releaseWait?.();
    clearTimeout(pendingWaitTimer);
    pendingWaitTimer = undefined;
    notify = undefined;
    clearTimeout(flushTimer);
    flushTimer = undefined;
    unprompted = undefined;
    try {
      channel.leave();
    } catch {
      // Leaving a channel on an already-dead socket throws; the disconnect
      // below is what actually matters.
    }
    socket.disconnect();
  };

  /**
   * Push one `msg:send` and collect the reply.
   *
   * The payload is pype's `pushMsgSend` (`xstate/helpers/phoenix.ts`), field
   * for field. There is no top-level `free_text`: the generated contract
   * (`pype/codegen/engagement-api.ts`, `MsgSendPayload`) has no such field.
   * `free_text` lives on the `text` APP OBJECT — `false` for an option pick,
   * `true` for typed text answering the bot (see `send`).
   */
  const sendMessage = async (message: {
    msg: string;
    app_object: Record<string, unknown> | null;
    msg_type: 'text' | 'embed';
  }): Promise<BotTurn> => {
    if (signal?.aborted) throw abortError();
    if (ended) {
      throw new ConversationError(
        'The conversation has already ended; no further messages can be sent.'
      );
    }

    const sentAt = Date.now();
    const payload = {
      from: session.userId,
      from_side: 'anonymous_consumer',
      gateway: 'pypestream_widget',
      ...message,
      user_id: session.userId,
      version: 'v2',
      access_token: session.accessToken,
      persist_to_history: true,
      secure_app_object: false,
      client_msg_id: uuid(),
    };
    await (sendVia
      ? sendVia(payload, session).catch((err: unknown) => {
          throw new ConversationError(
            `Couldn’t send the message: ${err instanceof Error ? err.message : String(err)}`
          );
        })
      : new Promise<void>((resolve, reject) => {
          channel
            .push('msg:send', payload)
            .receive('ok', () => resolve())
            .receive('error', (data: unknown) =>
              reject(
                new ConversationError(
                  `msg:send rejected: ${JSON.stringify(data)}`
                )
              )
            )
            .receive('timeout', () =>
              reject(new ConversationError('msg:send timed out'))
            );
        }));

    // With a live agent the reply comes when the agent writes it, through
    // `onUnprompted`; waiting for one here would time out as "the bot did not
    // reply".
    if (withAgent) return { text: '', options: [], frames: [] };

    // Non-null by construction: `required: true` throws rather than
    // returning null.
    const turn = (await collectTurn({
      required: true,
      timeoutMs: replyTimeoutMs,
    })) as BotTurn;

    return firstFrameAt === undefined
      ? turn
      : { ...turn, latencyMs: firstFrameAt - sentAt };
  };

  return {
    chatId: session.chatId,

    greeting: () =>
      collectTurn({ required: false, timeoutMs: greetingTimeoutMs }),

    send: (text: string) =>
      // Pype's composer (`pype-shell-controller.ts`, `_freeTextEmbedAnswer`):
      // typed text answering the bot's turn goes out as a `free_text: true`
      // `text` app object, which `pushMsgSend` sends as an `embed`. Without it
      // the bot engine reads the message as an option pick ("The user did not
      // enter text. Navigating based on child tag matching.") — a decision
      // node with no matching button falls through to the exit node and the
      // chat ends on the first message. Before the bot has spoken, the widget
      // sends plain text, and so does this.
      // Once a live agent has the chat, the widget sends plain text: the
      // embed is an answer to the BOT's question, and the agent desk shows it
      // as an encoded blob (pype's `isLatestMessageFromBot` gate).
      sendMessage(
        botSpoke && !withAgent
          ? {
              msg: text,
              app_object: {
                type: 'text',
                msg: text,
                bot_value: text,
                free_text: true,
              },
              msg_type: 'embed',
            }
          : { msg: text, app_object: null, msg_type: 'text' }
      ),

    sendMetadata: async (data) => {
      if (signal?.aborted) throw abortError();
      if (ended) {
        throw new ConversationError(
          'The conversation has already ended; no further messages can be sent.'
        );
      }
      // pype's `pushMetadataSend`, field for field.
      await new Promise<void>((resolve, reject) => {
        channel
          .push('metadata:send', {
            access_token: session.accessToken,
            data,
            version: 'v2',
          })
          .receive('ok', () => resolve())
          .receive('error', (err: unknown) =>
            reject(
              new ConversationError(
                `metadata:send rejected: ${JSON.stringify(err)}`
              )
            )
          )
          .receive('timeout', () =>
            reject(new ConversationError('metadata:send timed out'))
          );
      });
    },

    sendPlain: (text: string) =>
      sendMessage({ msg: text, app_object: null, msg_type: 'text' }),

    sendOption: (option: BotOption) =>
      // Pype's own option pick (`pype-core-element.ts`, `emitAction`): a
      // `text` app object carrying the label to show and the value to route
      // on, `free_text: false`, sent as an `embed`.
      sendMessage({
        msg: option.label,
        app_object: {
          type: 'text',
          msg: option.label,
          bot_value: option.value,
          free_text: false,
        },
        msg_type: 'embed',
      }),

    end: async () => {
      if (!ended) {
        // pype's `pushChatEnd`, field for field.
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, END_TIMEOUT_MS);
          const done = () => {
            clearTimeout(timer);
            resolve();
          };
          try {
            channel
              .push('chat:end', {
                ended_by: session.userId,
                pype_id: session.pypeId,
                source: 'consumer',
                user_id: session.userId,
                version: 'v2',
                access_token: session.accessToken,
              })
              .receive('ok', done)
              .receive('error', done)
              .receive('timeout', done);
          } catch {
            done();
          }
        });
        ended = true;
      }
      close();
    },

    close,

    isEnded: () => ended,

    isWithAgent: () => withAgent,

    onUnprompted: (listener) => {
      unprompted = listener;
      if (inbox.length || pendingNotices.length) scheduleFlush();
      return () => {
        if (unprompted === listener) unprompted = undefined;
      };
    },
  };
};
